/** Poll a queued Ace comparison export and download when ready — survives dialog close. */

import { useEffect, useRef } from "react";
import { agentReviewAPI } from "@/services/api/agentReview";

export type BackgroundAceExportState = {
  taskId: string;
  label: string;
  message: string;
  progress: number | null;
  startedAt: number;
  maxWaitMs: number;
  queuedBehindAnother?: boolean;
};

const STORAGE_KEY = "agent-review-background-ace-export";
const POLL_MS = 2_000;

export function loadPersistedAceExport(): BackgroundAceExportState | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BackgroundAceExportState;
    if (!parsed?.taskId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function persistAceExport(state: BackgroundAceExportState | null): void {
  if (typeof sessionStorage === "undefined") return;
  if (!state) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

type UseAceExportBackgroundTaskOptions = {
  state: BackgroundAceExportState | null;
  onUpdate: (patch: Partial<BackgroundAceExportState>) => void;
  onComplete: () => void;
  onError: (message: string) => void;
};

export function useAceExportBackgroundTask({
  state,
  onUpdate,
  onComplete,
  onError,
}: UseAceExportBackgroundTaskOptions): void {
  const onUpdateRef = useRef(onUpdate);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onUpdateRef.current = onUpdate;
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!state?.taskId) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const schedule = (delay: number) => {
      timeoutId = setTimeout(() => {
        void tick();
      }, delay);
    };

    const tick = async () => {
      if (cancelled || !state) return;

      if (Date.now() - state.startedAt > state.maxWaitMs) {
        onErrorRef.current(
          "Ace export is still running — check Ops → Task Manager or try again with fewer cases.",
        );
        return;
      }

      try {
        const taskId = state.taskId;
        const taskStatus = await agentReviewAPI.getAceComparisonExportStatus(taskId);
        if (cancelled) return;

        // If queued, keep polling this task until it runs (do not swap task ids based on global running exports).
        onUpdateRef.current({
          message: taskStatus.status_message ?? taskStatus.status,
          progress: taskStatus.progress ?? null,
          queuedBehindAnother: taskStatus.status === "queued",
        });

        if (taskStatus.status === "failed" || taskStatus.error) {
          onErrorRef.current(taskStatus.error ?? "Ace export failed");
          return;
        }

        if (taskStatus.ready) {
          try {
            await agentReviewAPI.downloadAceComparisonExportFile(taskId);
          } catch (err) {
            onErrorRef.current(err instanceof Error ? err.message : "Ace export download failed");
            return;
          }
          if (!cancelled) onCompleteRef.current();
          return;
        }

        if (taskStatus.status === "cancelled") {
          onErrorRef.current("Ace export was cancelled");
          return;
        }

        schedule(POLL_MS);
      } catch (err) {
        if (!cancelled) {
          onErrorRef.current(err instanceof Error ? err.message : "Ace export failed");
        }
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [state?.taskId, state?.startedAt, state?.maxWaitMs]);
}
